import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import API from '../services/api';

// Session state.
//
// Only the TOKEN is cached. The user object — and with it every capability flag — is
// refetched from /api/auth/me on each mount, so a role change takes effect on the next
// page load rather than persisting in a stale localStorage blob. Capabilities are read
// from the server's answer and never derived client-side: the server owns the mapping
// from role to what you may do, and a client that re-derived it would eventually
// disagree, showing a control that looks available and then fails.

const AuthContext = createContext(null);
export const TOKEN_KEY = 'gs_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    API.get('/api/auth/me/')
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username, password) => {
    const { data } = await API.post('/api/auth/login/', { username, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await API.post('/api/auth/logout/'); } catch (e) { /* a dead token still signs out locally */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    // Resolved server-side. Default to the safe answer if the payload is missing a field.
    canWrite: !!user?.can_write,
    canApprove: !!user?.can_approve,
    canReadAudit: !!user?.can_read_audit,
    // Publishing a policy document is an administrative act, not an ordinary write, so it
    // needs the role itself rather than can_write. Hiding the control is courtesy only —
    // IsDeltaAdmin refuses the request regardless of what the client believes.
    isAdmin: user?.role === 'admin',
  }), [user, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
